import {getLibraryQueue,subscribeLibrarySync} from '../utils/librarySync';
import {fetchWithAuth} from '../services/api';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getUserShelf, addToShelf as addToShelfApi, removeFromShelf as removeFromShelfApi, updateShelfItem as updateShelfItemApi } from '../services/api';
import { useUser } from './UserContext';
import { telegramHaptic } from '../utils/telegramExtras';
import { pendingRemovals, restoreRemovedItem } from '../utils/pendingRemovals';
import { showToast, dismissToast } from '../components/GlobalToast';
import { readOffline, writeOffline, updateOffline } from '../utils/offlineCache';
import i18n from '../i18n';

const ShelfContext = createContext();

// eslint-disable-next-line react-refresh/only-export-components
export const useShelf = () => {
  const context = useContext(ShelfContext);
  if (!context) {
    throw new Error('useShelf must be used within a ShelfProvider');
  }
  return context;
};

export const ShelfProvider = ({ children }) => {
  const { user } = useUser();
  return <AccountShelfProvider key={user?.id ?? 'guest'} userId={user?.id}>{children}</AccountShelfProvider>;
};

const AccountShelfProvider = ({ children, userId }) => {
  const [shelfItems, setShelfItems] = useState(() => readOffline('shelf', userId));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(()=>subscribeLibrarySync(()=>{
    const q=getLibraryQueue(fetchWithAuth);if(!q || q.getState().userId!==userId)return;
    const rows=q.snapshot('shelf');
    setShelfItems(rows.filter(item=>!pendingRemovals.has(`${userId}:shelf:${item.category}:${item.external_id}`)));
  }),[userId]);

  const loadShelf = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await getUserShelf();
      // Parse genres JSON for each item
      const parsed = (data || []).map(item => {
        if (item.genres && typeof item.genres === 'string') {
          try { item.genres = JSON.parse(item.genres); } catch { item.genres = []; }
        }
        return item;
      });
      setShelfItems(parsed.filter(item => !pendingRemovals.has(`${userId}:shelf:${item.category}:${item.external_id}`)));
      writeOffline('shelf', userId, parsed);
    } catch (error) {
      console.error('Ошибка загрузки полки:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Загружаем данные с бэкэнда при инициализации или входе/выходе пользователя
  useEffect(() => {
    if (userId) {
      loadShelf();
    } else {
      setShelfItems([]);
    }
  }, [userId, loadShelf]);
  useEffect(() => {
    window.addEventListener('online', loadShelf);
    return () => window.removeEventListener('online', loadShelf);
  }, [loadShelf]);

  const refreshShelf = useCallback(async () => {
    await loadShelf();
  }, [loadShelf]);

  const addToShelf = async (item, userRating = null) => {
    try {
      const restored = pendingRemovals.undo(`${userId}:shelf:${item.category}:${item.external_id ?? item.id}`);
      if (restored) return userRating == null ? restored : updateShelfItem(restored.id, restored.category, { user_rating: userRating });
      const existing = shelfItems.find(row => String(row.external_id) === String(item.external_id ?? item.id) && row.category === item.category);
      if (existing) return userRating == null ? existing : updateShelfItem(existing.id, existing.category, { user_rating: userRating });
      const genres = item.genres || [];

      // poster всегда сохраняем как есть (постер/обложка), backdrop отдельно
      const posterToSave = item.category === 'music' ? item.artwork : item.poster;

      const newItem = await addToShelfApi({
        external_id: item.external_id ?? item.id,
        category: item.category,
        title: item.title,
        original_title: item.originalTitle,
        overview: item.overview,
        poster: posterToSave || undefined,
        artwork: item.category === 'music' ? item.artwork : undefined,
        backdrop: item.backdrop || undefined,
        rating: item.rating,
        release_date: item.releaseDate,
        status: 'planned',
        ...(userRating != null ? { user_rating: userRating } : {}),
        genres: genres
      });

      // Parse genres for the new item (same as in loadShelf)
      if (newItem && newItem.genres && typeof newItem.genres === 'string') {
        try { newItem.genres = JSON.parse(newItem.genres); } catch { newItem.genres = []; }
      }

      setShelfItems(prev => [...prev.filter(row => row.id !== newItem.id), newItem]);
      updateOffline('shelf', userId, rows => [...rows.filter(row => row.id !== newItem.id), newItem]);
      telegramHaptic();
      return newItem;
    } catch (error) {
      console.error('Ошибка добавления на полку:', error);
      throw error;
    }
  };

  const removeFromShelf = async (itemId, category) => {
    try {
      // Находим элемент по external_id и category, чтобы получить его id
      const item = shelfItems.find(
        item => String(item.external_id) === String(itemId) && item.category === category
      );

      if (!item) {
        throw new Error('Элемент не найден на полке');
      }

      const key = `${userId}:shelf:${item.category}:${item.external_id}`;
      const token = localStorage.getItem('polka_token');
      const restore = () => setShelfItems(prev => restoreRemovedItem(prev, item, shelfItems));
      const toast = showToast({ title: i18n.t('ux.removedShelf'), message: item.title, duration: 8000,
        action: { label: i18n.t('ux.undo'), onClick: () => pendingRemovals.undo(key) } });
      pendingRemovals.add(key, {
        restore, value: item,
        commit: async keepalive => {
          await removeFromShelfApi(item.id, { keepalive, headers: { Authorization: `Bearer ${token}` } });
          updateOffline('shelf', userId, rows => rows.filter(row => row.id !== item.id));
        },
        failed: () => showToast({ title: i18n.t('ux.deleteFailed'), message: i18n.t('ux.deleteRestored'), type: 'error' }),
        finished: () => dismissToast(toast)
      });
      setShelfItems(prev =>
        prev.filter(item => !(String(item.external_id) === String(itemId) && item.category === category))
      );
    } catch (error) {
      console.error('Ошибка удаления с полки:', error);
      throw error;
    }
  };

  const isInShelf = (itemId, category) => {
    if (!shelfItems || shelfItems.length === 0) return false;
    return shelfItems.some(
      item => item && String(item.external_id) === String(itemId) && item.category === category
    );
  };

  const updateShelfItem = async (itemId, category, updates) => {
    try {
      const updatedItem = await updateShelfItemApi(itemId, updates);
      updateOffline('shelf', userId, rows => rows.map(row => String(row.id) === String(itemId) ? updatedItem : row));
      setShelfItems(prev =>
        prev.map(item =>
          String(item.id) === String(itemId) && item.category === category
            ? updatedItem
            : item
        )
      );
      return updatedItem;
    } catch (error) {
      console.error('Ошибка обновления элемента полки:', error);
      throw error;
    }
  };

  const getShelfByCategory = (category) => {
    if (category === 'all') {
      return shelfItems;
    }
    return shelfItems.filter(item => item.category === category);
  };

  const getStats = () => {
    return {
      total: shelfItems.length,
      films: shelfItems.filter(i => i.category === 'film').length,
      series: shelfItems.filter(i => i.category === 'series').length,
      games: shelfItems.filter(i => i.category === 'game').length,
      music: shelfItems.filter(i => i.category === 'music').length,
      books: shelfItems.filter(i => i.category === 'book').length
    };
  };

  return (
    <ShelfContext.Provider value={{
      shelfItems,
      loading,
      error,
      refreshShelf,
      addToShelf,
      removeFromShelf,
      isInShelf,
      updateShelfItem,
      getShelfByCategory,
      getStats
    }}>
      {children}
    </ShelfContext.Provider>
  );
};

export default ShelfContext;
